import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export const EventFeedback = ({ eventId, isPast }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");

  const load = () =>
    apiFetch(`/events/${eventId}/feedback`)
      .then((d) => {
        setData(d);
        setRating(d.my_rating || 0);
        setComment(d.my_comment || "");
      })
      .catch(() => {});

  useEffect(() => {
    if (isPast) load();
  }, [eventId, isPast]);

  if (!isPast || !data) return null;

  const submit = async () => {
    try {
      await apiFetch(`/events/${eventId}/feedback`, {
        method: "POST",
        body: JSON.stringify({ rating, comment }),
      });
      toast.success("Thanks — the host will see this");
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="mt-10 border-t border-neutral-200 pt-8" data-testid="event-feedback">
      <h2 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-neutral-900">
        <Star className="h-5 w-5 text-[#4D7C0F]" /> How was it?
      </h2>

      {data.average !== null && (
        <p className="mt-2 text-sm text-neutral-600" data-testid="feedback-average">
          <span className="text-lg font-bold text-neutral-900">{data.average}</span> / 5 from {data.count} attendee
          {data.count === 1 ? "" : "s"}
        </p>
      )}

      {data.can_rate ? (
        <div className="mt-4 rounded-2xl border border-neutral-200 p-5">
          <div className="flex gap-1" data-testid="rating-stars">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                onClick={() => setRating(n)}
                data-testid={`star-${n}`}
                aria-label={`${n} star`}
                className="p-1 transition-transform duration-200 hover:scale-110"
              >
                <Star className={`h-6 w-6 ${n <= rating ? "fill-[#84CC16] text-[#4D7C0F]" : "text-neutral-300"}`} />
              </button>
            ))}
          </div>
          <textarea
            data-testid="feedback-comment"
            rows={2}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Anything the host should know? (optional)"
            className="mt-3 w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm outline-none transition-colors duration-200 focus:border-neutral-900"
          />
          <button
            onClick={submit}
            disabled={rating === 0}
            data-testid="feedback-submit"
            className="mt-3 rounded-full bg-[#B8FF2E] px-5 py-2.5 text-sm font-bold text-[#14210A] transition-colors duration-200 hover:bg-[#A2E80B] disabled:opacity-50"
          >
            {data.my_rating ? "Update my rating" : "Send rating"}
          </button>
        </div>
      ) : (
        <p className="mt-3 text-sm text-neutral-500" data-testid="feedback-locked">
          Ratings come from attendees who RSVP'd.
        </p>
      )}

      {data.comments?.length > 0 && (
        <div className="mt-6" data-testid="feedback-comments">
          <p className="text-xs font-bold uppercase tracking-wide text-neutral-400">
            Attendee notes {user?.role !== "member" ? "(hosts only)" : ""}
          </p>
          <ul className="mt-3 space-y-3">
            {data.comments.map((c, i) => (
              <li key={i} className="rounded-xl bg-neutral-50 p-4">
                <p className="text-xs font-bold text-neutral-700">
                  {c.rating}/5 · {c.author.name}
                </p>
                <p className="mt-1 text-sm text-neutral-700">{c.comment}</p>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
